<script setup lang="ts">
import { Teambuilding } from '~/js/interfaces/teambuildings-tm'
import { getClasses } from '~/js/composables/classes'
import { getGlobalTranslation } from '~/js/composables/translations'

import BadgePrimary from '~/vue/_teammasters/atoms/badges/BadgePrimary.vue'
import BadgeSecondary from '~/vue/_teammasters/atoms/badges/BadgeSecondary.vue'
import ButtonTertiary from '~/vue/_teammasters/atoms/buttons/ButtonTertiary.vue'
import ImageTeaser from '~/vue/_teammasters/atoms/images/ImageTeaser.vue'
import LinkOverlay from '~/vue/_teammasters/atoms/links/LinkOverlay.vue'
import TextH4 from '~/vue/_teammasters/atoms/texts/TextH4.vue'
import TextProse from '~/vue/_teammasters/atoms/texts/TextProse.vue'

interface Props {
    card: Teambuilding,
    utilities?: string,
    theme?: string|null,
}

withDefaults(defineProps<Props>(), {
    utilities: '',
    theme: null,
})

const classes = {
    wrapper: {
        _default: 'relative group/card-teambuilding overflow-hidden',
        animation: 'transition-colors',
        border: 'border border-neutral-100',
        color: 'bg-white dark:bg-neutral-900',
        flex: 'flex flex-col',
        focus: 'focus-within:ring-2 focus-within:ring-offset-white dark:ring-offset-black focus-within:ring-offset-2 focus-within:outline-none focus-within:ring-offset-red-light dark:focus-within:ring-offset-black',
        hover: 'hover:bg-neutral-900 hover:dark:bg-white',
        rounded: 'rounded-3xl',
        shadow: 'shadow-teammasters',
        size: 'w-full',
    },
    image: {
        rounded: 'rounded-t-3xl',
    },
    content: {
        spacing: 'p-4 space-y-4',
        flex: 'flex flex-col grow',
        hover: 'group-hover/card-teambuilding:!text-white'
    },
    badges: {
        flex: 'flex flex-wrap gap-2',
    },
    hover: {
        _default: 'group-hover/card-teambuilding:text-white group-hover/card-teambuilding:dark:text-neutral-900'
    }
}
</script>

<template>
    <article
        :class="[
            getClasses(classes,'wrapper'),
            utilities
        ]"
        :data-theme="theme"
    >
        <ImageTeaser
            v-if="card.teaser_image"
            :image="card.teaser_image"
            :sizes="['25vw']"
            :utilities="getClasses(classes, 'image')"
            ratio="aspect-[3/2] md:aspect-video"
        />

        <div :class="getClasses(classes, 'content')">
            <h3>
                <TextH4 
                    :theme="theme"
                    :utilities="getClasses(classes, 'hover')"
                >{{ card.title }}</TextH4>
            </h3>

            <TextProse 
                color="text-neutral-700 dark:text-white"
                :utilities="`block ${getClasses(classes, 'hover')}`"
            >
                <span v-html="card.description"/>
            </TextProse>

            <div :class="getClasses(classes, 'badges')">
                <BadgePrimary
                    v-for="category in card.categories_full"
                    :key="category.id"
                    :utilities="`group-hover/card-teambuilding:border-white group-hover/card-teambuilding:dark:border-neutral-900 ${getClasses(classes, 'hover')} `"
                >
                    {{ category.title }}
                </BadgePrimary>
                <BadgePrimary
                    v-for="category in card.seasonals_full"
                    :key="category.id"
                    :utilities="`group-hover/card-teambuilding:border-white group-hover/card-teambuilding:dark:border-neutral-900 ${getClasses(classes, 'hover')} `"
                >
                    {{ category.title }}
                </BadgePrimary>
                <BadgePrimary
                    v-for="category in card.purposes_full"
                    :key="category.id"
                    :utilities="`group-hover/card-teambuilding:border-white group-hover/card-teambuilding:dark:border-neutral-900 ${getClasses(classes, 'hover')} `"
                >
                    {{ category.title }}
                </BadgePrimary>
                <BadgeSecondary v-if="card.new">{{ getGlobalTranslation('new') }}</BadgeSecondary>
            </div>

            <ButtonTertiary 
                :content="getGlobalTranslation('View teambuilding')"
                :utilities="`!mt-auto ${getClasses(classes, 'hover')}`"
                type="div"
            />
        </div>

        <LinkOverlay
            :ga="{
                category: 'card-teambuilding',
                action: 'click',
                label: card.title
            }"
            :title="card.title"
            :url="card.url"
        />
    </article>
</template>